import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, Clock, Phone, Loader2, Tag, ExternalLink } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import WhatsAppButton from "../components/WhatsAppButton";
import { api } from "../lib/api";
import { businessInfo } from "../mock";
import { useSeo, useJsonLd, breadcrumbSchema, SITE_URL, DEFAULT_TITLE } from "../lib/seo";

const mdComponents = {
  h2: (p) => <h2 className="mt-10 mb-3 text-2xl font-bold text-white" {...p} />,
  h3: (p) => <h3 className="mt-8 mb-2 text-xl font-semibold text-white" {...p} />,
  p: (p) => <p className="my-4 leading-relaxed text-emerald-50/80" {...p} />,
  ul: (p) => <ul className="my-4 pl-5 list-disc space-y-1.5 text-emerald-50/80 marker:text-emerald-400" {...p} />,
  ol: (p) => <ol className="my-4 pl-5 list-decimal space-y-1.5 text-emerald-50/80 marker:text-emerald-400" {...p} />,
  strong: (p) => <strong className="text-white font-semibold" {...p} />,
  a: (p) => <a className="text-emerald-300 underline underline-offset-2" {...p} />,
  blockquote: (p) => <blockquote className="my-5 border-l-2 border-emerald-400 pl-4 italic text-emerald-100/80" {...p} />,
};

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setPost(null);
    setError(false);
    api
      .get(`/blog/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setError(true));
  }, [slug]);

  useSeo({
    title: post ? `${post.title} | Repairzone Π.Κ Λαμία` : DEFAULT_TITLE,
    description: post ? post.excerpt : "",
    path: `/blog/${slug}`,
    image: post ? post.image : undefined,
    type: "article",
  });
  useJsonLd(
    "blog-post",
    post && {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: post.title,
      description: post.excerpt,
      image: post.image,
      datePublished: post.created_at,
      author: { "@type": "Organization", name: "Repairzone Π.Κ" },
      publisher: { "@type": "Organization", name: "Repairzone Π.Κ", url: SITE_URL },
      mainEntityOfPage: `${SITE_URL}/blog/${post.slug}`,
      keywords: post.tags.join(", "),
    }
  );
  useJsonLd(
    "blog-bc",
    post && breadcrumbSchema([{ name: "Αρχική", path: "/" }, { name: "Blog", path: "/blog" }, { name: post.title, path: `/blog/${post.slug}` }])
  );

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="pt-24 pb-24" data-testid="blog-post-page">
        {error && (
          <div className="max-w-3xl mx-auto px-5 pt-16 text-center" data-testid="blog-post-not-found">
            <h1 className="text-3xl font-bold text-white">Το άρθρο δεν βρέθηκε</h1>
            <Link to="/blog" className="btn-primary rounded-md px-5 py-2.5 inline-flex items-center gap-2 mt-6">
              <ArrowLeft className="w-4 h-4" /> Πίσω στο blog
            </Link>
          </div>
        )}
        {!post && !error && (
          <div className="mt-24 flex justify-center text-emerald-300">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        )}
        {post && (
          <article className="max-w-3xl mx-auto px-5 lg:px-8">
            <Link
              to="/blog"
              data-testid="blog-back-link"
              className="inline-flex items-center gap-2 text-sm text-emerald-300 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> Όλα τα άρθρα
            </Link>
            <div className="mt-6 flex flex-wrap items-center gap-3 text-xs text-emerald-200/70">
              <span className="uppercase tracking-wider px-2.5 py-1 rounded-md bg-emerald-500/15 border border-emerald-400/40 text-emerald-200">
                {post.category_label}
              </span>
              <span>
                {new Date(post.created_at).toLocaleDateString("el-GR", { day: "numeric", month: "long", year: "numeric" })}
              </span>
              <span className="inline-flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" /> {post.read_minutes} λεπτά ανάγνωσης
              </span>
            </div>
            <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-tight" data-testid="blog-post-title">
              {post.title}
            </h1>
            <p className="mt-4 text-lg text-emerald-50/75">{post.excerpt}</p>
            <div className="mt-8 rounded-2xl overflow-hidden border border-emerald-500/20 aspect-[16/9]">
              <img src={post.image} alt={post.title} width="1264" height="848" decoding="async" className="w-full h-full object-cover" />
            </div>
            <div className="mt-8" data-testid="blog-post-content">
              <ReactMarkdown components={mdComponents}>{post.content}</ReactMarkdown>
            </div>
            {post.source_url && (
              <a
                href={post.source_url}
                target="_blank"
                rel="noreferrer nofollow"
                data-testid="blog-post-source"
                className="mt-8 flex items-start gap-3 rounded-md border border-emerald-900/70 bg-emerald-500/5 px-4 py-3 text-sm text-emerald-100/75 hover:border-emerald-500/50 transition-colors"
              >
                <ExternalLink className="w-4 h-4 mt-0.5 shrink-0 text-emerald-300" />
                <span>
                  <span className="text-emerald-300">Πηγή:</span> {post.source_name} — {post.source_title}
                </span>
              </a>
            )}
            {post.tags.length > 0 && (
              <div className="mt-10 flex flex-wrap gap-2">
                {post.tags.map((t) => (
                  <span key={t} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md border border-emerald-900/70 text-emerald-100/70">
                    <Tag className="w-3 h-3" /> {t}
                  </span>
                ))}
              </div>
            )}
            <div className="mt-12 pcb-card p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-5 justify-between">
              <div>
                <div className="text-white font-bold text-lg">Χρειάζεστε βοήθεια με τη συσκευή σας;</div>
                <div className="text-sm text-emerald-50/70 mt-1">
                  Δωρεάν διάγνωση, κατ' οίκον εξυπηρέτηση σε Λαμία & Φθιώτιδα, 6 μήνες εγγύηση.
                </div>
              </div>
              <a
                href={`tel:${businessInfo.mobile}`}
                data-testid="blog-cta-call"
                className="btn-primary rounded-md px-5 py-3 inline-flex items-center justify-center gap-2 shrink-0"
              >
                <Phone className="w-4 h-4" /> {businessInfo.mobile}
              </a>
            </div>
          </article>
        )}
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default BlogPostPage;
